import { PageLoaderRegistry } from '@cosuno/cosuno-ui';
import mapboxgl from 'mapbox-gl';
import React, { memo, useMemo } from 'react';

import AppPage from '~/components/App/AppPage';
import MessagePage from '~/shared/components/MessagePage';
import { mapboxToken } from '~/shared/constants/mapbox';
import useMaintenanceMode from '~/shared/hooks/useMaintenanceMode';
import useTranslation from '~/shared/hooks/useTranslation';

import AppContent from './AppContent';
import BaseStyles from './BaseStyles';
import BeamerScript from './BeamerScript';
import { BootstrapStyles } from './BootstrapStyles';
import CelloScript from './CelloScript';
import Contexts from './Contexts';
import NormalizeStyles from './NormalizeStyles';
import Router from './Router';

if (mapboxToken) {
  mapboxgl.accessToken = mapboxToken;
}

const AppInMaintenanceMode: React.FC = () => {
  const { t } = useTranslation();
  return (
    <>
      <NormalizeStyles />
      <BaseStyles />
      <MessagePage
        isAuthenticated={false}
        pageTitle={t('maintenance:title')}
        heading={t('maintenance:title')}
        message={t('maintenance:explanation')}
      />
    </>
  );
};

let loaderTimeout: NodeJS.Timeout | number | undefined = 0;
const handlePageLoaderUpdate = (currentLoaderCount: number) => {
  clearTimeout(loaderTimeout);
  if (currentLoaderCount > 0) {
    document.body.classList.add('is-loading');
  } else {
    loaderTimeout = setTimeout(() => {
      document.body.classList.remove('is-loading');
    }, 100);
  }
};

const AppInOnlineMode = memo(() => (
  <BootstrapStyles>
    <PageLoaderRegistry subscribe={handlePageLoaderUpdate}>
      <Router Page={AppPage}>
        {/*
          this useMemo helps to mitigate the new architecture of our router which requires us to
          rerender the whole contexts stack for each navigation. This can lead to weird ui glitches since authentication
          info is reinitialized if not memoized
          */}
        {useMemo(
          () => (
            <Contexts>
              <BeamerScript />
              <CelloScript />
              <AppContent />
            </Contexts>
          ),
          [],
        )}
      </Router>
    </PageLoaderRegistry>
  </BootstrapStyles>
));

const App: React.FC = () => {
  const { isEnabled: isMaintenanceModeEnabled } = useMaintenanceMode();

  if (isMaintenanceModeEnabled) {
    return <AppInMaintenanceMode />;
  }

  return <AppInOnlineMode />;
};

export default App;
